<template>
  <body data-sidebar="dark">
  <!-- Begin page -->
  <div id="layout-wrapper">
    <!-- Left Sidebar -->
    <NavigationBar/>

    <!-- Main Content -->
    <div class="main-content">
      <div class="page-content">
        <div class="container-fluid">
          <!-- Page Title -->
          <div class="row">
            <div class="col-12">
              <div class="page-title-box d-sm-flex align-items-center justify-content-between">
                <h4 class="mb-sm-0 font-size-18">User Profile</h4>
                <div class="page-title-right">
                  <ol class="breadcrumb m-0">
                    <li class="breadcrumb-item"><router-link to="/dashboard">Dashboard</router-link></li>
                    <li class="breadcrumb-item active">Profile</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>

          <!-- Alerts -->
          <div v-if="alert.success" class="alert alert-success alert-dismissible fade show" role="alert">
            {{ message }}
            <button type="button" class="btn-close" @click="clearAlerts"></button>
          </div>
          <div v-if="alert.info" class="alert alert-primary alert-dismissible fade show" role="alert">
            {{ message }}
            <button type="button" class="btn-close" @click="clearAlerts"></button>
          </div>
          <div v-if="alert.failed" class="alert alert-danger alert-dismissible fade show" role="alert">
            {{ message }}
            <button type="button" class="btn-close" @click="clearAlerts"></button>
          </div>

          <!-- Loading State -->
          <div v-if="loading" class="text-center py-5">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
            <p class="mt-3">Loading profile...</p>
          </div>

          <!-- Profile Content -->
          <div v-else class="row">
            <!-- Profile Sidebar -->
            <div class="col-xxl-4 col-xl-5">
              <div class="card">
                <div class="card-body">
                  <!-- Profile Picture Section -->
                  <div class="text-center mb-4">
                    <div class="position-relative d-inline-block">
                      <img
                          :src="profilePictureUrl"
                          alt="Profile Picture"
                          class="rounded-circle avatar-xl"
                          @error="handleImageError"
                      >
                      <button
                          class="btn btn-light btn-sm position-absolute bottom-0 end-0 rounded-circle"
                          style="width: 35px; height: 35px;"
                          @click="$refs.profilePictureInput.click()"
                          title="Change Profile Picture"
                      >
                        <i class="mdi mdi-camera"></i>
                      </button>
                      <input
                          ref="profilePictureInput"
                          type="file"
                          accept="image/*"
                          style="display: none"
                          @change="handleProfilePictureUpload"
                      >
                    </div>
                    <h5 class="mt-3 mb-1">{{ fullName }}</h5>
                    <p class="text-muted mb-0">{{ profile.username || 'User' }}</p>
                    <p class="text-muted mb-3">{{ profile.organization_name || '' }}</p>

                    <!-- Status Badge -->
                    <span class="badge" :class="{
                        'bg-success': profile.is_active == '1',
                        'bg-danger': profile.is_active != '1'
                      }">
                        {{ profile.is_active == '1' ? 'Active' : 'Inactive' }}
                      </span>
                  </div>

                  <!-- Quick Info -->
                  <div class="table-responsive">
                    <table class="table table-borderless mb-0">
                      <tbody>
                      <tr>
                        <td class="fw-medium">Email:</td>
                        <td>{{ profile.email }}</td>
                      </tr>
                      <tr>
                        <td class="fw-medium">Phone:</td>
                        <td>{{ profile.phone }}</td>
                      </tr>
                      <tr>
                        <td class="fw-medium">Gender:</td>
                        <td>{{ capitalizeFirst(profile.gender) }}</td>
                      </tr>
                      <tr>
                        <td class="fw-medium">Join Date:</td>
                        <td>{{ formatDate(profile.created_at) }}</td>
                      </tr>
                      <tr>
                        <td class="fw-medium">Last Login:</td>
                        <td>{{ formatDateTime(profile.last_login) }}</td>
                      </tr>
                      </tbody>
                    </table>
                  </div>

                  <!-- Action Buttons -->
                  <div class="mt-4">
                    <button class="btn btn-outline-secondary w-100" @click="switchTab('change-password')">
                      <i class="mdi mdi-lock-reset me-1"></i>
                      Change Password
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Profile Details -->
            <div class="col-xxl-8 col-xl-7">
              <div class="card">
                <div class="card-header">
                  <!-- Nav tabs -->
                  <ul class="nav nav-tabs card-header-tabs" role="tablist">
                    <li class="nav-item" role="presentation">
                      <a class="nav-link active" data-bs-toggle="tab" href="#profile-overview" role="tab">
                        <i class="mdi mdi-account-circle me-1"></i>
                        Overview
                      </a>
                    </li>
                    <li class="nav-item" role="presentation">
                      <a class="nav-link" data-bs-toggle="tab" href="#edit-profile" role="tab">
                        <i class="mdi mdi-account-edit me-1"></i>
                        Edit Profile
                      </a>
                    </li>
                    <li class="nav-item" role="presentation">
                      <a class="nav-link" data-bs-toggle="tab" href="#change-password" role="tab">
                        <i class="mdi mdi-lock-reset me-1"></i>
                        Security
                      </a>
                    </li>
                    <li class="nav-item" role="presentation">
                      <a class="nav-link" data-bs-toggle="tab" href="#activity-log" role="tab">
                        <i class="mdi mdi-history me-1"></i>
                        Activity
                      </a>
                    </li>
                  </ul>
                </div>

                <div class="card-body">
                  <div class="tab-content">
                    <!-- Overview Tab -->
                    <div class="tab-pane fade show active" id="profile-overview" role="tabpanel">
                      <h5 class="mb-3">Personal Information</h5>
                      <div class="row">
                        <div class="col-md-6 mb-3">
                          <label class="form-label fw-medium">Full Name:</label>
                          <p class="text-muted">{{ fullName }}</p>
                        </div>
                        <div class="col-md-6 mb-3">
                          <label class="form-label fw-medium">Username:</label>
                          <p class="text-muted">{{ profile.username }}</p>
                        </div>
                        <div class="col-md-6 mb-3">
                          <label class="form-label fw-medium">Email Address:</label>
                          <p class="text-muted">{{ profile.email }}</p>
                        </div>
                        <div class="col-md-6 mb-3">
                          <label class="form-label fw-medium">Phone Number:</label>
                          <p class="text-muted">{{ profile.phone }}</p>
                        </div>
                        <div class="col-md-6 mb-3">
                          <label class="form-label fw-medium">Gender:</label>
                          <p class="text-muted">{{ capitalizeFirst(profile.gender) }}</p>
                        </div>
                        <div class="col-md-6 mb-3">
                          <label class="form-label fw-medium">Date of Birth:</label>
                          <p class="text-muted">{{ formatDate(profile.date_of_birth) }}</p>
                        </div>
                        <div class="col-md-6 mb-3">
                          <label class="form-label fw-medium">National ID:</label>
                          <p class="text-muted">{{ profile.national_id || 'Not provided' }}</p>
                        </div>
                        <div class="col-md-6 mb-3">
                          <label class="form-label fw-medium">Employee ID:</label>
                          <p class="text-muted">{{ profile.employee_id || 'Not provided' }}</p>
                        </div>
                      </div>

                      <h5 class="mb-3 mt-4">Organization Details</h5>
                      <div class="row">
                        <div class="col-md-6 mb-3">
                          <label class="form-label fw-medium">Organization:</label>
                          <p class="text-muted">{{ profile.organization_name }}</p>
                        </div>
                        <div class="col-md-6 mb-3">
                          <label class="form-label fw-medium">Department:</label>
                          <p class="text-muted">{{ profile.department_name || 'Not assigned' }}</p>
                        </div>
                        <div class="col-md-6 mb-3">
                          <label class="form-label fw-medium">Organization Level:</label>
                          <p class="text-muted">{{ capitalizeFirst(profile.organization_level) }}</p>
                        </div>
                        <div class="col-md-6 mb-3">
                          <label class="form-label fw-medium">Access Scope:</label>
                          <p class="text-muted">{{ capitalizeFirst(profile.access_scope) }}</p>
                        </div>
                        <div class="col-md-6 mb-3">
                          <label class="form-label fw-medium">Region:</label>
                          <p class="text-muted">{{ profile.region_name || 'Not assigned' }}</p>
                        </div>
                        <div class="col-md-6 mb-3">
                          <label class="form-label fw-medium">Zone:</label>
                          <p class="text-muted">{{ profile.zone_name || 'Not assigned' }}</p>
                        </div>
                        <div class="col-md-6 mb-3">
                          <label class="form-label fw-medium">Woreda:</label>
                          <p class="text-muted">{{ profile.woreda_name || 'Not assigned' }}</p>
                        </div>
                      </div>

                      <h5 class="mb-3 mt-4">Additional Information</h5>
                      <div class="row" v-if="profile.profileData">
                        <div class="col-md-12 mb-3">
                          <label class="form-label fw-medium">Bio:</label>
                          <p class="text-muted">{{ profile.profileData.bio || 'No bio provided' }}</p>
                        </div>
                        <div class="col-md-6 mb-3">
                          <label class="form-label fw-medium">Blood Group:</label>
                          <p class="text-muted">{{ profile.profileData.bloodGroup || 'Not provided' }}</p>
                        </div>
                        <div class="col-md-6 mb-3">
                          <label class="form-label fw-medium">Languages:</label>
                          <p class="text-muted">{{ profile.profileData.languages || 'Not provided' }}</p>
                        </div>
                        <div class="col-md-6 mb-3">
                          <label class="form-label fw-medium">Allergies:</label>
                          <p class="text-muted">{{ profile.profileData.allergies || 'None reported' }}</p>
                        </div>
                        <div class="col-md-6 mb-3">
                          <label class="form-label fw-medium">Medical Conditions:</label>
                          <p class="text-muted">{{ profile.profileData.medicalConditions || 'None reported' }}</p>
                        </div>
                        <div class="col-md-12 mb-3">
                          <label class="form-label fw-medium">Certifications:</label>
                          <p class="text-muted">{{ profile.profileData.certifications || 'None listed' }}</p>
                        </div>
                      </div>

                      <h5 class="mb-3 mt-4">Emergency Contact</h5>
                      <div class="row">
                        <div class="col-md-6 mb-3">
                          <label class="form-label fw-medium">Contact Name:</label>
                          <p class="text-muted">{{ profile.emergency_contact_name || 'Not provided' }}</p>
                        </div>
                        <div class="col-md-6 mb-3">
                          <label class="form-label fw-medium">Contact Phone:</label>
                          <p class="text-muted">{{ profile.emergency_contact_phone || 'Not provided' }}</p>
                        </div>
                        <div class="col-md-6 mb-3">
                          <label class="form-label fw-medium">Relationship:</label>
                          <p class="text-muted">{{ profile.emergency_contact_relationship || 'Not provided' }}</p>
                        </div>
                      </div>
                    </div>

                    <!-- Edit Profile Tab -->
                    <div class="tab-pane fade" id="edit-profile" role="tabpanel">
                      <form @submit.prevent="updateProfile">
                        <h5 class="mb-3">Personal Information</h5>
                        <div class="row">
                          <div class="col-md-12 mb-3">
                            <label for="fullName" class="form-label">Full Name <span class="text-danger">*</span></label>
                            <input type="text" class="form-control" id="fullName" v-model="editForm.fullName" required>
                          </div>
                          <div class="col-md-6 mb-3">
                            <label for="email" class="form-label">Email Address <span class="text-danger">*</span></label>
                            <input type="email" class="form-control" id="email" v-model="editForm.email" required>
                          </div>
                          <div class="col-md-6 mb-3">
                            <label for="phone" class="form-label">Phone Number <span class="text-danger">*</span></label>
                            <input type="tel" class="form-control" id="phone" v-model="editForm.phone" required>
                          </div>
                          <div class="col-md-6 mb-3">
                            <label for="gender" class="form-label">Gender</label>
                            <select class="form-select" id="gender" v-model="editForm.gender">
                              <option value="">Select Gender</option>
                              <option value="Male">Male</option>
                              <option value="Female">Female</option>
                              <option value="Other">Other</option>
                            </select>
                          </div>
                          <div class="col-md-6 mb-3">
                            <label for="dateOfBirth" class="form-label">Date of Birth</label>
                            <input type="date" class="form-control" id="dateOfBirth" v-model="editForm.dateOfBirth">
                          </div>
                          <div class="col-md-6 mb-3">
                            <label for="nationalId" class="form-label">National ID</label>
                            <input type="text" class="form-control" id="nationalId" v-model="editForm.nationalId">
                          </div>
                          <div class="col-md-6 mb-3">
                            <label for="employeeId" class="form-label">Employee ID</label>
                            <input type="text" class="form-control" id="employeeId" v-model="editForm.employeeId">
                          </div>
                        </div>

                        <h5 class="mb-3 mt-4">Emergency Contact</h5>
                        <div class="row">
                          <div class="col-md-6 mb-3">
                            <label for="emergencyContactName" class="form-label">Emergency Contact Name</label>
                            <input type="text" class="form-control" id="emergencyContactName" v-model="editForm.emergencyContactName">
                          </div>
                          <div class="col-md-6 mb-3">
                            <label for="emergencyContactPhone" class="form-label">Emergency Contact Phone</label>
                            <input type="tel" class="form-control" id="emergencyContactPhone" v-model="editForm.emergencyContactPhone">
                          </div>
                          <div class="col-md-12 mb-3">
                            <label for="emergencyContactRelationship" class="form-label">Relationship</label>
                            <input type="text" class="form-control" id="emergencyContactRelationship" v-model="editForm.emergencyContactRelationship" placeholder="e.g., Spouse, Parent, Sibling">
                          </div>
                        </div>

                        <h5 class="mb-3 mt-4">Additional Information</h5>
                        <div class="row">
                          <div class="col-md-12 mb-3">
                            <label for="bio" class="form-label">Bio</label>
                            <textarea class="form-control" id="bio" rows="3" v-model="editForm.profileData.bio" placeholder="Tell us about yourself..."></textarea>
                          </div>
                          <div class="col-md-6 mb-3">
                            <label for="bloodGroup" class="form-label">Blood Group</label>
                            <select class="form-select" id="bloodGroup" v-model="editForm.profileData.bloodGroup">
                              <option value="">Select Blood Group</option>
                              <option value="A+">A+</option>
                              <option value="A-">A-</option>
                              <option value="B+">B+</option>
                              <option value="B-">B-</option>
                              <option value="AB+">AB+</option>
                              <option value="AB-">AB-</option>
                              <option value="O+">O+</option>
                              <option value="O-">O-</option>
                            </select>
                          </div>
                          <div class="col-md-6 mb-3">
                            <label for="languages" class="form-label">Languages Spoken</label>
                            <input type="text" class="form-control" id="languages" v-model="editForm.profileData.languages" placeholder="e.g., English, Amharic, Oromo">
                          </div>
                          <div class="col-md-6 mb-3">
                            <label for="allergies" class="form-label">Allergies</label>
                            <input type="text" class="form-control" id="allergies" v-model="editForm.profileData.allergies" placeholder="List any known allergies">
                          </div>
                          <div class="col-md-6 mb-3">
                            <label for="medicalConditions" class="form-label">Medical Conditions</label>
                            <input type="text" class="form-control" id="medicalConditions" v-model="editForm.profileData.medicalConditions" placeholder="List any medical conditions">
                          </div>
                          <div class="col-md-12 mb-3">
                            <label for="certifications" class="form-label">Certifications</label>
                            <textarea class="form-control" id="certifications" rows="2" v-model="editForm.profileData.certifications" placeholder="List your professional certifications"></textarea>
                          </div>
                        </div>

                        <div class="text-end">
                          <button type="button" class="btn btn-light me-2" @click="resetEditForm">Cancel</button>
                          <button type="submit" class="btn btn-primary" :disabled="updating">
                            <span v-if="updating" class="spinner-border spinner-border-sm me-2"></span>
                            {{ updating ? 'Updating...' : 'Update Profile' }}
                          </button>
                        </div>
                      </form>
                    </div>

                    <!-- Change Password Tab -->
                    <div class="tab-pane fade" id="change-password" role="tabpanel">
                      <form @submit.prevent="changePassword">
                        <h5 class="mb-3">Change Password</h5>
                        <div class="row">
                          <div class="col-md-12 mb-3">
                            <label for="currentPassword" class="form-label">Current Password <span class="text-danger">*</span></label>
                            <div class="input-group">
                              <input
                                  :type="showCurrentPassword ? 'text' : 'password'"
                                  class="form-control"
                                  id="currentPassword"
                                  v-model="passwordForm.currentPassword"
                                  required
                              >
                              <button type="button" class="btn btn-outline-secondary" @click="showCurrentPassword = !showCurrentPassword">
                                <i :class="showCurrentPassword ? 'mdi mdi-eye-off' : 'mdi mdi-eye'"></i>
                              </button>
                            </div>
                          </div>
                          <div class="col-md-6 mb-3">
                            <label for="newPassword" class="form-label">New Password <span class="text-danger">*</span></label>
                            <div class="input-group">
                              <input
                                  :type="showNewPassword ? 'text' : 'password'"
                                  class="form-control"
                                  id="newPassword"
                                  v-model="passwordForm.newPassword"
                                  required
                                  minlength="8"
                              >
                              <button type="button" class="btn btn-outline-secondary" @click="showNewPassword = !showNewPassword">
                                <i :class="showNewPassword ? 'mdi mdi-eye-off' : 'mdi mdi-eye'"></i>
                              </button>
                            </div>
                            <small class="form-text text-muted">Password must be at least 8 characters long</small>
                          </div>
                          <div class="col-md-6 mb-3">
                            <label for="confirmPassword" class="form-label">Confirm New Password <span class="text-danger">*</span></label>
                            <div class="input-group">
                              <input
                                  :type="showConfirmPassword ? 'text' : 'password'"
                                  class="form-control"
                                  id="confirmPassword"
                                  v-model="passwordForm.confirmPassword"
                                  required
                                  minlength="8"
                              >
                              <button type="button" class="btn btn-outline-secondary" @click="showConfirmPassword = !showConfirmPassword">
                                <i :class="showConfirmPassword ? 'mdi mdi-eye-off' : 'mdi mdi-eye'"></i>
                              </button>
                            </div>
                            <small v-if="passwordForm.newPassword && passwordForm.confirmPassword && passwordForm.newPassword !== passwordForm.confirmPassword" class="form-text text-danger">
                              Passwords do not match
                            </small>
                          </div>
                        </div>

                        <div class="alert alert-info">
                          <h6 class="alert-heading">Password Requirements:</h6>
                          <ul class="mb-0">
                            <li>At least 8 characters long</li>
                            <li>Cannot be the same as your last 3 passwords</li>
                            <li>Should contain a mix of letters, numbers, and special characters</li>
                          </ul>
                        </div>

                        <div class="text-end">
                          <button type="button" class="btn btn-light me-2" @click="resetPasswordForm">Cancel</button>
                          <button type="submit" class="btn btn-primary" :disabled="changingPassword || passwordForm.newPassword !== passwordForm.confirmPassword">
                            <span v-if="changingPassword" class="spinner-border spinner-border-sm me-2"></span>
                            {{ changingPassword ? 'Changing...' : 'Change Password' }}
                          </button>
                        </div>
                      </form>

                      <hr class="my-4">

                      <h5 class="mb-3">Password History</h5>
                      <p class="text-muted">Last password changed: {{ formatDateTime(profile.password_changed_at) }}</p>
                    </div>

                    <!-- Activity Log Tab -->
                    <div class="tab-pane fade" id="activity-log" role="tabpanel">
                      <div class="d-flex justify-content-between align-items-center mb-3">
                        <h5 class="mb-0">Recent Activity</h5>
                        <button class="btn btn-sm btn-outline-primary" @click="loadActivityLog">
                          <i class="mdi mdi-refresh me-1"></i>
                          Refresh
                        </button>
                      </div>

                      <div v-if="loadingActivity" class="text-center py-4">
                        <div class="spinner-border spinner-border-sm text-primary"></div>
                        <p class="mt-2 text-muted">Loading activity...</p>
                      </div>

                      <div v-else class="timeline">
                        <div v-for="activity in activityLog" :key="activity.logId" class="timeline-item">
                          <div class="timeline-marker">
                            <i :class="getActivityIcon(activity.action)" class="text-primary"></i>
                          </div>
                          <div class="timeline-content">
                            <div class="d-flex justify-content-between">
                              <h6 class="mb-1">{{ activity.action }}</h6>
                              <small class="text-muted">{{ formatDateTime(activity.createdAt) }}</small>
                            </div>
                            <p class="text-muted mb-1">{{ activity.module }}</p>
                            <small class="text-muted">IP: {{ activity.ipAddress }}</small>
                          </div>
                        </div>
                      </div>

                      <div v-if="activityLog.length === 0 && !loadingActivity" class="text-center py-4">
                        <i class="mdi mdi-history display-1 text-muted"></i>
                        <p class="text-muted">No recent activity found</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <footer class="footer">
        <div class="container-fluid">
          <div class="row">
            <div class="col-sm-6">© CBHI</div>
            <div class="col-sm-6">
              <div class="text-sm-end d-sm-block">
                Ministry of Health - Ethiopia
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  </div>
  </body>
</template>

<script>
/* eslint-disable */
import NavigationBar from "@/components/Navigation.vue";
import { authUrl, base, executeJson } from "@/api";

export default {
  name: "UserProfile",
  components: {
    NavigationBar
  },
  data() {
    return {
      loading: false,
      updating: false,
      changingPassword: false,
      loadingActivity: false,

      // Profile data
      profile: {},
      activityLog: [],

      // Edit form - aligned with API response structure
      editForm: {
        fullName: '',
        email: '',
        phone: '',
        gender: '',
        dateOfBirth: '',
        nationalId: '',
        employeeId: '',
        emergencyContactName: '',
        emergencyContactPhone: '',
        emergencyContactRelationship: '',
        profileData: {
          bio: '',
          bloodGroup: '',
          languages: '',
          allergies: '',
          medicalConditions: '',
          certifications: ''
        }
      },

      // Password form
      passwordForm: {
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      },

      // Password visibility
      showCurrentPassword: false,
      showNewPassword: false,
      showConfirmPassword: false,

      // Alert states
      alert: {
        success: false,
        info: false,
        failed: false
      },
      message: ''
    };
  },

  computed: {
    fullName() {
      return this.profile.full_name || 'Loading...';
    },

    profilePictureUrl() {
      return this.profile.profile_picture ? base + this.profile.profile_picture : './assets/images/users/default-avatar.png';
    }
  },

  mounted() {
    this.loadUserProfile();
  },

  methods: {
    clearAlerts() {
      this.alert.success = false;
      this.alert.info = false;
      this.alert.failed = false;
    },

    showAlert(type, message) {
      this.clearAlerts();
      this.alert[type] = true;
      this.message = message;

      if (type === 'success' || type === 'info') {
        setTimeout(() => {
          this.clearAlerts();
        }, 5000);
      }
    },

    async loadUserProfile() {
      this.loading = true;

      const data = {
        function: "getUserProfile"
      };

      try {
        const res = await executeJson(data, authUrl);

        if (res.data.success) {
          this.profile = res.data.data.profile;
          this.populateEditForm();
        } else {
          this.showAlert('failed', res.data.message || 'Failed to load profile');
        }
      } catch (error) {
        this.showAlert('failed', error.message || 'An error occurred while loading profile');
      } finally {
        this.loading = false;
      }
    },

    populateEditForm() {
      this.editForm = {
        fullName: this.profile.full_name || '',
        email: this.profile.email || '',
        phone: this.profile.phone || '',
        gender: this.profile.gender || '',
        dateOfBirth: this.profile.date_of_birth || '',
        nationalId: this.profile.national_id || '',
        employeeId: this.profile.employee_id || '',
        emergencyContactName: this.profile.emergency_contact_name || '',
        emergencyContactPhone: this.profile.emergency_contact_phone || '',
        emergencyContactRelationship: this.profile.emergency_contact_relationship || '',
        profileData: {
          bio: this.profile.profileData?.bio || '',
          bloodGroup: this.profile.profileData?.bloodGroup || '',
          languages: this.profile.profileData?.languages || '',
          allergies: this.profile.profileData?.allergies || '',
          medicalConditions: this.profile.profileData?.medicalConditions || '',
          certifications: this.profile.profileData?.certifications || ''
        }
      };
    },

    async updateProfile() {
      this.updating = true;

      const data = {
        function: "updateUserProfile",
        ...this.editForm
      };

      try {
        const res = await executeJson(data, authUrl);

        if (res.data.success) {
          this.profile = res.data.data.profile;
          this.showAlert('success', 'Profile updated successfully');
          this.switchTab('profile-overview');
        } else {
          this.showAlert('failed', res.data.message || 'Failed to update profile');
        }
      } catch (error) {
        this.showAlert('failed', error.message || 'An error occurred while updating profile');
      } finally {
        this.updating = false;
      }
    },

    async changePassword() {
      this.changingPassword = true;

      const data = {
        function: "changePassword",
        ...this.passwordForm
      };

      try {
        const res = await executeJson(data, authUrl);

        if (res.data.success) {
          this.showAlert('success', 'Password changed successfully');
          this.resetPasswordForm();
          this.switchTab('profile-overview');
        } else {
          this.showAlert('failed', res.data.message || 'Failed to change password');
        }
      } catch (error) {
        this.showAlert('failed', error.message || 'An error occurred while changing password');
      } finally {
        this.changingPassword = false;
      }
    },

    async handleProfilePictureUpload(event) {
      const file = event.target.files[0];
      if (!file) return;

      // Validate file type
      if (!file.type.startsWith('image/')) {
        this.showAlert('failed', 'Please select a valid image file');
        return;
      }

      // Validate file size (2MB max)
      if (file.size > 2 * 1024 * 1024) {
        this.showAlert('failed', 'File size must be less than 2MB');
        return;
      }

      const formData = new FormData();
      formData.append('profilePicture', file);
      formData.append('function', 'uploadProfilePicture');

      try {
        const response = await fetch(authUrl, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`
          },
          body: formData
        });

        const res = await response.json();

        if (res.success) {
          this.profile.profile_picture = res.data.profilePicture;
          this.showAlert('success', 'Profile picture updated successfully');
        } else {
          this.showAlert('failed', res.message || 'Failed to upload profile picture');
        }
      } catch (error) {
        this.showAlert('failed', 'An error occurred while uploading profile picture');
      }
    },

    async loadActivityLog() {
      this.loadingActivity = true;

      const data = {
        function: "getUserActivityLog",
        limit: 20,
        offset: 0
      };

      try {
        const res = await executeJson(data, authUrl);

        if (res.data.success) {
          this.activityLog = res.data.data.activityLog;
        } else {
          this.showAlert('failed', res.data.message || 'Failed to load activity log');
        }
      } catch (error) {
        this.showAlert('failed', error.message || 'An error occurred while loading activity log');
      } finally {
        this.loadingActivity = false;
      }
    },

    switchTab(tabName) {
      const tabElement = document.querySelector(`a[href="#${tabName}"]`);
      if (tabElement && typeof bootstrap !== 'undefined') {
        const tab = new bootstrap.Tab(tabElement);
        tab.show();
      }
    },

    resetEditForm() {
      this.populateEditForm();
    },

    resetPasswordForm() {
      this.passwordForm = {
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      };
      this.showCurrentPassword = false;
      this.showNewPassword = false;
      this.showConfirmPassword = false;
    },

    handleImageError(event) {
      event.target.src = './assets/images/users/default-avatar.png';
    },

    // Utility methods
    formatDate(date) {
      if (!date) return 'Not provided';
      return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    },

    formatDateTime(datetime) {
      if (!datetime) return 'Never';
      return new Date(datetime).toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    capitalizeFirst(str) {
      if (!str) return '';
      return str.charAt(0).toUpperCase() + str.slice(1);
    },

    getActivityIcon(action) {
      const iconMap = {
        'login': 'mdi mdi-login',
        'logout': 'mdi mdi-logout',
        'create': 'mdi mdi-plus-circle',
        'update': 'mdi mdi-pencil',
        'delete': 'mdi mdi-delete',
        'view': 'mdi mdi-eye'
      };
      return iconMap[action.toLowerCase()] || 'mdi mdi-information';
    }
  }
};
</script>

<style scoped>
.avatar-xl {
  width: 120px;
  height: 120px;
  object-fit: cover;
}

.timeline {
  position: relative;
  padding-left: 30px;
}

.timeline-item {
  position: relative;
  margin-bottom: 20px;
}

.timeline-marker {
  position: absolute;
  left: -30px;
  top: 0;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background-color: #fff;
  border: 2px solid #e9ecef;
  display: flex;
  align-items: center;
  justify-content: center;
}

.timeline-marker i {
  font-size: 10px;
}

.timeline-content {
  background-color: #f8f9fa;
  padding: 15px;
  border-radius: 8px;
  border-left: 3px solid #007bff;
}

.timeline::before {
  content: '';
  position: absolute;
  left: -21px;
  top: 0;
  bottom: 0;
  width: 2px;
  background-color: #e9ecef;
}

.badge {
  font-size: 0.75rem;
}

.nav-tabs .nav-link {
  color: #6c757d;
  border-bottom: 2px solid transparent;
}

.nav-tabs .nav-link.active {
  color: #495057;
  border-bottom-color: #007bff;
}

.btn:disabled {
  opacity: 0.65;
}

.spinner-border-sm {
  width: 1rem;
  height: 1rem;
}

.form-label {
  font-weight: 500;
  margin-bottom: 0.5rem;
}

.text-danger {
  color: #dc3545 !important;
}

.alert {
  border: none;
  border-radius: 8px;
}

.card {
  border: none;
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
}

.table th {
  font-weight: 600;
  color: #495057;
  border-top: none;
}

.table-hover tbody tr:hover {
  background-color: rgba(0, 0, 0, 0.02);
}

@media (max-width: 768px) {
  .avatar-xl {
    width: 80px;
    height: 80px;
  }

  .timeline {
    padding-left: 20px;
  }

  .timeline-marker {
    left: -20px;
    width: 15px;
    height: 15px;
  }

  .timeline::before {
    left: -13px;
  }
}
</style>